import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Shield, CheckCircle2, AlertTriangle, Lock, FileText, 
  Activity, Users, Clock, Eye, RefreshCw, Server, Database
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComplianceCheckCard = ({ check }) => {
  const isCompliant = check.status === 'compliant';
  
  return (
    <div className={`p-4 rounded-lg border ${isCompliant ? 'border-green-500/30 bg-green-500/5' : 'border-yellow-500/30 bg-yellow-500/5'}`}>
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isCompliant ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
          {isCompliant ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">{check.category}</Badge>
            <h4 className="font-medium">{check.check}</h4>
          </div>
          <p className="text-sm text-muted-foreground">{check.details}</p>
        </div>
      </div>
    </div>
  );
};

const AuditLogRow = ({ entry }) => {
  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-green-500';
      case 'UPDATE': return 'bg-blue-500';
      case 'DELETE': return 'bg-red-500';
      case 'READ': return 'bg-gray-500';
      case 'EXPORT': return 'bg-purple-500';
      case 'IMPORT': return 'bg-cyan-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-0">
      <Badge className={`${getActionColor(entry.action)} text-white text-xs`}>
        {entry.action}
      </Badge>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{entry.resource_type}</p>
        <p className="text-xs text-muted-foreground">{entry.user_email}</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-muted-foreground">
          {new Date(entry.timestamp).toLocaleString()}
        </p>
        {entry.phi_accessed && (
          <Badge variant="outline" className="text-xs mt-1">PHI</Badge>
        )}
      </div>
    </div>
  );
};

export default function Compliance() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [complianceData, setComplianceData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch compliance status
      const complianceRes = await fetch(`${API_URL}/api/compliance/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (complianceRes.ok) {
        setComplianceData(await complianceRes.json());
      }

      // Fetch audit logs
      const auditRes = await fetch(`${API_URL}/api/audit-logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (auditRes.ok) {
        setAuditLogs(await auditRes.json());
      }

      // Fetch audit summary
      const summaryRes = await fetch(`${API_URL}/api/audit-logs/summary?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (summaryRes.ok) {
        setAuditSummary(await summaryRes.json());
      }
    } catch (error) {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="compliance-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            Security & Compliance
          </h1>
          <p className="text-muted-foreground mt-1">HIPAA, SOC2 & FHIR compliance dashboard</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-compliance">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Compliance Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="overflow-hidden">
          <div className={`h-1 ${complianceData?.overall_status === 'compliant' ? 'bg-green-500' : 'bg-yellow-500'}`} />
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Compliance Score</p>
                <p className="text-4xl font-bold">{complianceData?.compliance_score || 0}%</p>
              </div>
              <div className={`h-14 w-14 rounded-full flex items-center justify-center ${complianceData?.overall_status === 'compliant' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                {complianceData?.overall_status === 'compliant' ? (
                  <CheckCircle2 className="h-7 w-7 text-green-500" />
                ) : (
                  <AlertTriangle className="h-7 w-7 text-yellow-500" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-cyan-500 to-teal-500" />
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Checks Passed</p>
                <p className="text-4xl font-bold">
                  {complianceData?.compliant_checks}/{complianceData?.total_checks}
                </p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-cyan-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" />
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Audit Events (7d)</p>
                <p className="text-4xl font-bold">{auditSummary?.total_events || 0}</p>
              </div>
              <Activity className="h-10 w-10 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">PHI Access Events</p>
                <p className="text-4xl font-bold">
                  {auditLogs.filter(l => l.phi_accessed).length}
                </p>
              </div>
              <Eye className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Compliance Progress</span>
            <span className="text-sm text-muted-foreground">{complianceData?.compliance_score || 0}%</span>
          </div>
          <Progress value={complianceData?.compliance_score || 0} className="h-3" />
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Shield className="h-4 w-4 mr-2" />
            Compliance Checks
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <FileText className="h-4 w-4 mr-2" />
            Audit Log
          </TabsTrigger>
          <TabsTrigger value="fhir" data-testid="tab-fhir">
            <Server className="h-4 w-4 mr-2" />
            FHIR Status
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {complianceData?.checks?.map((check, idx) => (
              <ComplianceCheckCard key={idx} check={check} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Audit Activity</CardTitle>
              <CardDescription>
                All data access and modifications are logged for HIPAA compliance
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Activity Summary */}
              {auditSummary?.action_counts && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {Object.entries(auditSummary.action_counts).map(([action, count]) => (
                    <div key={action} className="text-center p-3 bg-muted rounded-lg">
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground uppercase">{action}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Audit Log List */}
              <ScrollArea className="h-96">
                {auditLogs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No audit logs available</p>
                ) : (
                  auditLogs.map((entry, idx) => (
                    <AuditLogRow key={idx} entry={entry} />
                  ))
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fhir" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  FHIR R4 Endpoints
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div>
                    <p className="font-medium">GET /api/fhir/metadata</p>
                    <p className="text-xs text-muted-foreground">Capability Statement</p>
                  </div>
                  <Badge className="bg-green-500">Active</Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div>
                    <p className="font-medium">GET /api/fhir/Patient</p>
                    <p className="text-xs text-muted-foreground">Search Patients</p>
                  </div>
                  <Badge className="bg-green-500">Active</Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div>
                    <p className="font-medium">POST /api/fhir/Patient</p>
                    <p className="text-xs text-muted-foreground">Create Patient</p>
                  </div>
                  <Badge className="bg-green-500">Active</Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div>
                    <p className="font-medium">GET /api/fhir/Observation</p>
                    <p className="text-xs text-muted-foreground">Quality Metrics as Observations</p>
                  </div>
                  <Badge className="bg-green-500">Active</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  FHIR Resource Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                      <Users className="h-6 w-6 text-cyan-500" />
                    </div>
                    <div>
                      <p className="font-medium">Patient Resource</p>
                      <p className="text-sm text-muted-foreground">
                        Full CRUD operations with hospice extensions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <Activity className="h-6 w-6 text-purple-500" />
                    </div>
                    <div>
                      <p className="font-medium">Observation Resource</p>
                      <p className="text-sm text-muted-foreground">
                        Quality metrics mapped to FHIR Observations
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Lock className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">Security</p>
                      <p className="text-sm text-muted-foreground">
                        Bearer token authentication on all endpoints
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
